import { useEffect, useMemo, useState } from "react";
import TenantAPI from "../../../../api/api/TenantAPI";
import { useQuery } from "@tanstack/react-query";
import { Module } from "@/types/TenantInterfaces";
import useGlobalLists from "../../cloud/useGlobalLists";
import useAllTags from "../../../../hooks/useAllTags";
import { EMPTY_ID } from "../../../../utils/Constants";

export type FieldGroup = {
  Name: string;
  Value: string;
};

const empty: Array<any> = [];

// Used for fetching the options of a Group By Field
// Used for ordering the Kanban Board Groups
// Examples - A Select Select field's options
//          - All records selected for a Single Reference field (in the select module)
// "Calculate" because for example, Single Reference / String field options have to be calculated
const useCalculateFieldGroups = (
  tenantID: string | null,
  workspaceID: string | undefined,
  fieldAPIName: string | null,
  moduleAPIName: string | null,
  includeEmpty: boolean = false,
  savedFieldAPIName: string | undefined,
  savedOrder: Array<string> | undefined,
  module?: Module
) => {
  const [orderedGroups, setOrderedGroups] = useState<Array<FieldGroup>>([]);
  const { globalLists, globalListsLoading } = useGlobalLists();
  const { tags, tagsLoading } = useAllTags(
    moduleAPIName,
    fieldAPIName !== "Tags"
  );

  const field = useMemo(() => {
    if (!module || !fieldAPIName) return;

    return module.Fields.find((f) => f.APIName === fieldAPIName);
  }, [module?.Fields, fieldAPIName]);

  const fieldName = field?.Name;
  const fieldType = field?.FieldType;

  // Get the SourcesList for Single/Multi Select fields
  // For Global Lists, we need to find the list, and add an empty group to it (If configured)
  const sourcesList = useMemo(() => {
    if (
      !["SingleSelect", "MultiSelect"].includes(fieldType!) ||
      !field?.SourcesList
    ) {
      return null;
    }

    if (!Array.isArray(field?.SourcesList)) {
      if (globalListsLoading) return null;

      const listName = field?.SourcesList?.split("Global.Lists.")?.[1];

      if (!listName) return null;

      const globalList = structuredClone(
        globalLists?.find((l) => l.APIName === listName)
      );

      if (!globalList) return [];

      return globalList?.Variables;
    }

    return structuredClone(field?.SourcesList);
  }, [
    field?.SourcesList,
    fieldAPIName,
    fieldType,
    includeEmpty,
    globalLists,
    globalListsLoading,
  ]);

  const fieldGroups = useQuery({
    queryKey: ["fieldGroups", tenantID, fieldAPIName, moduleAPIName],
    queryFn: async () => {
      if (["SingleSelect", "MultiSelect"].includes(fieldType!)) {
        return sourcesList;
      }

      // This function won't be called unless all the parameters exist. See `enabled` flag below.
      const data = await TenantAPI.getFieldGroups(
        workspaceID!,
        fieldAPIName!,
        moduleAPIName!
      );

      return parseGroups(data.data.data.groups);
    },
    enabled: !!tenantID && !!fieldAPIName && !!workspaceID && !!fieldType,
  });

  // Ensures Options have the Empty option or not, and follow the Saved Order
  useEffect(() => {
    const finalSavedOrder =
      savedFieldAPIName !== fieldAPIName ? undefined : savedOrder;

    if (Array.isArray(sourcesList)) {
      const orderedOptions = GetOrderedGroups(
        sourcesList,
        fieldName!,
        finalSavedOrder,
        includeEmpty
      );

      return setOrderedGroups(orderedOptions);
    }

    if (fieldType === "Tags") {
      if (tagsLoading) return;

      const allTags = tags?.map((t: any) => ({ Name: t.Name, Value: t.id }));

      const orderedTags = GetOrderedGroups(
        allTags || [],
        "Tags",
        finalSavedOrder,
        includeEmpty
      );

      return setOrderedGroups(orderedTags);
    }

    if (fieldGroups.isLoading) return;

    // Ordering by all other supported field types
    const orderedGroups = GetOrderedGroups(
      fieldGroups.data || [],
      fieldName!,
      finalSavedOrder,
      includeEmpty
    );

    setOrderedGroups(orderedGroups);
  }, [
    fieldGroups.data,
    fieldGroups.isLoading,
    includeEmpty,
    savedOrder,
    tags,
    fieldName,
    fieldType,
    tagsLoading,
    sourcesList,
    fieldAPIName,
  ]);

  function parseGroups(groups: { _id: string }[]) {
    if (!groups || !Array.isArray(groups)) return [];

    return groups.map((g) => ({ Name: g["_id"], Value: g["_id"] }));
  }

  function reorderGroups({ newOrder }: { newOrder: Array<string> }) {
    const newGroups = newOrder.flatMap((group) => {
      const foundGroup = orderedGroups.find((g) => g.Value === group);

      return foundGroup ? [foundGroup] : [];
    });

    setOrderedGroups(newGroups);
  }

  function deleteGroup({ id: groupValue }: { id: string }) {
    setOrderedGroups(
      orderedGroups.filter((group) => group.Value !== groupValue)
    );
  }

  function resetGroups() {
    let groupData;

    if (Array.isArray(sourcesList)) {
      groupData = sourcesList;
    } else if (fieldType === "Tags") {
      if (tagsLoading) return;
      groupData = tags?.map((t: any) => ({ Name: t.Name, Value: t.id }));
    } else {
      if (fieldGroups.isLoading) return;
      groupData = fieldGroups.data || [];
    }

    setOrderedGroups(
      GetOrderedGroups(groupData, fieldName!, undefined, includeEmpty)
    );
  }

  return {
    fieldGroups: orderedGroups || empty,
    groupsLoading: fieldGroups.isLoading,
    groupsError: fieldGroups.error,
    reorderGroups: reorderGroups,
    deleteGroup: deleteGroup,
    resetGroups: resetGroups,
  };
};

function GetOrderedGroups(
  sourcesList: { Name: string; Value: string }[],
  fieldName: string,
  savedOrder: string[] | undefined,
  includeEmpty: boolean
) {
  if (!savedOrder) {
    const finalOrder = structuredClone(sourcesList);

    if (includeEmpty) {
      finalOrder.push({ Name: `No ${fieldName}`, Value: EMPTY_ID });
    }

    return finalOrder;
  }

  let finalOrder = savedOrder.flatMap((group: any) => {
    if (group === EMPTY_ID) {
      return { Name: `No ${fieldName}`, Value: EMPTY_ID };
    }

    const foundVariable = sourcesList.find((s: any) => s.Value === group);

    return foundVariable ? [foundVariable] : [];
  });

  if (
    includeEmpty &&
    !finalOrder.find((group: any) => group.Value === EMPTY_ID)
  ) {
    finalOrder.push({ Name: `No ${fieldName}`, Value: EMPTY_ID });
  }

  if (!includeEmpty) {
    finalOrder = finalOrder.filter((group: any) => group.Value !== EMPTY_ID);
  }

  return finalOrder;
}

export default useCalculateFieldGroups;
